import { useState, useEffect, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsAppFloatingButton } from "@/components/WhatsAppFloatingButton";
import { SEO } from "@/components/SEO";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { fetchPublicOfferings } from "@/lib/revenuecat";
import type { Package } from "@revenuecat/purchases-js";

import {
  EnarmCountdownBanner,
  LandingNav,
  HeroSection,
  MetricsSection,
  FeaturesSection,
  SocialRankSection,
  ContentSourcesSection,
  WorkflowSection,
  PricingPlansSection,
  FinalCtaSection,
  LandingFooter,
} from "@/components/landing";

const BlogPreviewList = lazy(() =>
  import("@/components/coming-soon/BlogPreviewList").then((m) => ({
    default: m.BlogPreviewList,
  }))
);

export function LandingPage() {
  const { seoSettings, socialLinks } = usePlatformSettings();
  const [queriesEnabled, setQueriesEnabled] = useState(false);
  const [offerings, setOfferings] = useState<Package[]>([]);

  const { enabled: landingBlogVisible } = useFeatureFlag(
    "landing_blog_visible",
    true,
    { enabled: queriesEnabled }
  );

  const { enabled: socialEnabled } = useFeatureFlag(
    "social_features_enabled",
    false,
    { enabled: queriesEnabled }
  );

  const { posts, isLoading: blogLoading } = useBlogPosts({
    limit: 3,
    status: "published",
    enabled: queriesEnabled && landingBlogVisible,
  });

  const hasPosts = posts && posts.length > 0;

  useEffect(() => {
    const enableQueries = () => {
      setQueriesEnabled(true);
      window.removeEventListener("scroll", enableQueries);
      window.removeEventListener("mousemove", enableQueries);
      window.removeEventListener("touchstart", enableQueries);
    };

    window.addEventListener("scroll", enableQueries, { passive: true });
    window.addEventListener("mousemove", enableQueries, { passive: true });
    window.addEventListener("touchstart", enableQueries, { passive: true });

    const timer = setTimeout(() => {
      setQueriesEnabled(true);
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", enableQueries);
      window.removeEventListener("mousemove", enableQueries);
      window.removeEventListener("touchstart", enableQueries);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPublicOfferings().then(setOfferings).catch(console.error);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen max-w-full overflow-x-hidden bg-background text-foreground">
      <SEO
        title={
          seoSettings.title ||
          "RepasoGPC | Flashcards y simuladores para complementar tu estudio ENARM"
        }
        description={
          seoSettings.description ||
          "Complementa tu preparación para el ENARM con flashcards tipo Anki, simuladores y fuentes médicas mexicanas claramente identificadas."
        }
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "RepasoGPC",
          applicationCategory: "EducationalApplication",
          operatingSystem: "Web",
          url: "https://repasogpc.com",
        }}
      />

      <EnarmCountdownBanner />

      <LandingNav
        socialEnabled={socialEnabled}
        landingBlogVisible={landingBlogVisible}
      />

      <main>
        <HeroSection />
        <MetricsSection />
        <FeaturesSection />
        {socialEnabled && <SocialRankSection />}
        <ContentSourcesSection />
        <WorkflowSection />
        <PricingPlansSection offerings={offerings} />

        {landingBlogVisible && !blogLoading && hasPosts && (
          <section id="blog" className="py-20 lg:py-28">
            <div className="container mx-auto px-4">
              <div className="mb-10 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
                <div>
                  <p className="text-sm font-bold uppercase text-primary">Recursos</p>
                  <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Lecturas para tu preparación</h2>
                </div>
                <Link to="/blog">
                  <Button variant="outline">
                    Ver artículos
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <BlogPreviewList />
              </Suspense>
            </div>
          </section>
        )}

        <FinalCtaSection />
      </main>

      <LandingFooter
        socialLinks={socialLinks}
        socialEnabled={socialEnabled}
      />

      <WhatsAppFloatingButton />
    </div>
  );
}
